/**
 * The OAuth return leg.
 *
 * Outbound, a screen writes a pending marker and sends the consent redirect off
 * with `returnTo: location.href`. The CRM callback then either redirects back
 * verbatim (success — NO query param is appended, except a first RingCentral
 * sign-in's `?sms_default=<prompt>`, which is a question rather than a verdict)
 * or appends `?oauth_error=<code>` to that URL (failure). Abandoned consents
 * (>15 min) never come back at all — they die on a plain error page at the CRM.
 *
 * `captureOauthReturn` runs as the FIRST statement in main.tsx, before the
 * router mounts: it reads the param, strips it from the address bar via
 * history.replaceState (no reload), and parks it for a one-shot
 * `consumeOauthReturn` by whichever screen the route lands on.
 *
 * The parsing core is pure and unit-tested; only the two thin shells touch
 * window.
 */
import type { SmsDefaultPrompt } from '../api/types';

export const OAUTH_ERROR_CODES = [
  'no_identity',
  'account_mismatch',
  'email_taken',
  'number_not_on_account',
  'access_denied',
  'no_code',
] as const;

export type OauthErrorCode = (typeof OAUTH_ERROR_CODES)[number];

/** The answers a first RingCentral sign-in can carry back (services/smsDefault.js). */
const SMS_DEFAULT_PROMPTS: readonly SmsDefaultPrompt[] = ['auto', 'ask_replace', 'ask_enable'];

export interface OauthReturn {
  /** null = the return leg succeeded (no param). */
  error: OauthErrorCode | string | null;
}

export interface ParsedReturn {
  oauthReturn: OauthReturn | null;
  /**
   * An Instagram CONNECT does not finish at the callback: it comes back with a
   * short-lived grant the connect screen redeems over an authenticated request
   * (channels/instagram/login.js). Stripped from the address bar like the error
   * — a grant in the history is a grant somebody can find.
   */
  instagramGrant: string | null;
  /**
   * A first RingCentral sign-in's answer to "what does this number mean for
   * automated SMS" — the return panel asks the question it names. Null for any
   * value it does not know: a question the console cannot word is no question.
   */
  smsDefault: SmsDefaultPrompt | null;
  /** The URL to replaceState to, when a param had to be stripped. */
  cleanedUrl: string | null;
}

/** Pure: does this search string carry an OAuth return, and what's the clean URL. */
export function parseOauthReturn(
  search: string,
  pathname: string,
  hash: string,
): ParsedReturn {
  const params = new URLSearchParams(search);
  const error = params.get('oauth_error');
  const instagramGrant = params.get('instagram_grant');
  const rawSmsDefault = params.get('sms_default');
  if (error === null && instagramGrant === null && rawSmsDefault === null) {
    return { oauthReturn: null, instagramGrant: null, smsDefault: null, cleanedUrl: null };
  }

  params.delete('oauth_error');
  params.delete('instagram_grant');
  params.delete('sms_default');
  const rest = params.toString();
  return {
    oauthReturn: error === null ? null : { error },
    instagramGrant,
    smsDefault: SMS_DEFAULT_PROMPTS.find((prompt) => prompt === rawSmsDefault) ?? null,
    cleanedUrl: pathname + (rest ? `?${rest}` : '') + hash,
  };
}

export function messageForOauthError(error: string): string {
  switch (error) {
    case 'no_identity':
      return 'The provider did not return an account identity — try again and complete the consent screen fully.';
    case 'account_mismatch':
      return 'You signed in with a different account than this channel is bound to. Sign in with the matching account.';
    case 'no_mailbox':
      return 'That Microsoft account has no Outlook mailbox to connect — it was probably created with an address from another provider. Sign in with the account the mailbox belongs to.';
    case 'email_taken':
      return 'That mailbox is already connected to LimoSphere CRM. A mailbox can only be linked to one account at a time — disconnect the existing channel before connecting it here.';
    case 'number_not_on_account':
      return 'That phone number is not on the RingCentral account you signed into.';
    case 'access_denied':
      return 'The sign-in was cancelled before access was granted. Start it again when you are ready.';
    case 'no_code':
      return 'The provider came back without a sign-in code — try again and complete the consent screen fully.';
    default:
      return `The provider sign-in failed (${error}).`;
  }
}

// ------------------------------------------------------------- pending marker

/** What the console remembers across the consent redirect. */
export interface PendingOauth {
  inboxId: number;
  slug: string;
  at: number;
}

/** Older than this and the marker is stale — the state JWT died at 15 min. */
export const PENDING_MAX_AGE_MS = 30 * 60 * 1000;

export function parsePendingMarker(raw: string | null, now: number): PendingOauth | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingOauth>;
    if (
      typeof parsed.inboxId !== 'number' ||
      typeof parsed.slug !== 'string' ||
      typeof parsed.at !== 'number'
    ) {
      return null;
    }
    if (now - parsed.at > PENDING_MAX_AGE_MS) return null;
    return { inboxId: parsed.inboxId, slug: parsed.slug, at: parsed.at };
  } catch {
    return null;
  }
}

// ------------------------------------------------------- window-touching shell

const MARKER_KEY = 'console.pendingOauth';

let captured: OauthReturn | null = null;
let capturedGrant: string | null = null;
let capturedSmsDefault: SmsDefaultPrompt | null = null;

/** Call once, before the router mounts. */
export function captureOauthReturn(loc: Location): void {
  const { oauthReturn, instagramGrant, smsDefault, cleanedUrl } = parseOauthReturn(
    loc.search,
    loc.pathname,
    loc.hash,
  );
  captured = oauthReturn;
  capturedGrant = instagramGrant;
  capturedSmsDefault = smsDefault;
  if (cleanedUrl !== null) {
    window.history.replaceState(null, '', cleanedUrl);
  }
}

/** Non-destructive read of a relayed Instagram grant — safe under StrictMode double renders. */
export function peekInstagramGrant(): string | null {
  return capturedGrant;
}

export function clearInstagramGrant(): void {
  capturedGrant = null;
}

/** Non-destructive read of a sign-in's default-sender question — safe under StrictMode. */
export function peekSmsDefaultReturn(): SmsDefaultPrompt | null {
  return capturedSmsDefault;
}

export function clearSmsDefaultReturn(): void {
  capturedSmsDefault = null;
}

/** One-shot read of a captured return. */
export function consumeOauthReturn(): OauthReturn | null {
  const value = captured;
  captured = null;
  return value;
}

/** Non-destructive read — safe under StrictMode double renders. */
export function peekOauthReturn(): OauthReturn | null {
  return captured;
}

export function clearOauthReturn(): void {
  captured = null;
}

export function writePendingMarker(marker: Omit<PendingOauth, 'at'>): void {
  try {
    sessionStorage.setItem(
      MARKER_KEY,
      JSON.stringify({ ...marker, at: Date.now() }),
    );
  } catch {
    // Storage unavailable — the return leg degrades to the home-screen toast.
  }
}

export function takePendingMarker(): PendingOauth | null {
  const marker = peekPendingMarker();
  clearPendingMarker();
  return marker;
}

/** Non-destructive read — safe under StrictMode double renders. */
export function peekPendingMarker(): PendingOauth | null {
  try {
    return parsePendingMarker(sessionStorage.getItem(MARKER_KEY), Date.now());
  } catch {
    return null;
  }
}

export function clearPendingMarker(): void {
  try {
    sessionStorage.removeItem(MARKER_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
